import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import BaseModal from './BaseModal';

// In-app replacements for the browser's window.confirm / window.alert popups,
// rendered through BaseModal so they match the app theme.
//
//   const { confirm, alert } = useDialog();
//   if (!(await confirm({ title, message, confirmLabel, danger: true }))) return;
//   await alert({ title, message });
//
// `confirm` resolves true/false with the user's choice; `alert` shows a single
// OK button and resolves once dismissed. Escape / clicking the overlay counts
// as Cancel. The provider sits above the router, so an open dialog survives
// navigation.
const DialogContext = createContext(null);

export const useDialog = () => useContext(DialogContext);

export const DialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setDialog({ kind: 'confirm', ...options, resolve });
      }),
    [],
  );

  const alert = useCallback(
    (options) =>
      new Promise((resolve) => {
        const normalized = typeof options === 'string' ? { message: options } : options;
        setDialog({ kind: 'alert', title: 'Notice', confirmLabel: 'OK', ...normalized, resolve });
      }),
    [],
  );

  const closeDialog = (result) => {
    dialog.resolve(result);
    setDialog(null);
  };

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <DialogContext.Provider value={value}>
      {children}
      {dialog && (
        <BaseModal
          open
          titleId="app-dialog-title"
          title={dialog.title}
          message={dialog.message}
          confirmLabel={dialog.confirmLabel || 'Confirm'}
          cancelLabel={dialog.cancelLabel || 'Cancel'}
          danger={!!dialog.danger}
          showCancel={dialog.kind === 'confirm'}
          onSubmit={(e) => {
            e.preventDefault();
            closeDialog(true);
          }}
          onCancel={() => closeDialog(false)}
        />
      )}
    </DialogContext.Provider>
  );
};
